import * as z from "zod";
import { crmAccountDto, listCrmAccountsInput } from "./account";
import { crmCampaignSummaryDto } from "./campaign";
import { sendPauseReasons } from "./engagement/enums";
import { noShowRisks } from "./insights";

// "Today", the home of Guestlist (ADR-299 §7, ADR-287 §6): what to do first, from the projection of crm in one read.
// The campaigns still running with their invitations nobody answered, the appointments of the next days, and the
// customers nothing moved for a while; for one territory when an area manager picks theirs (no default, B-153).

/** How far the home looks: the appointments ahead, and the time without movement that makes an account quiet. */
export const CRM_TODAY_WINDOWS = { appointmentDays: 7, quietDays: 90 } as const;

/** How many of each part the home shows at most: the rest is in the lists of the campaigns and of the accounts. */
export const CRM_TODAY_LIMITS = { campaigns: 10, appointments: 25, quietAccounts: 25, sending: 5 } as const;

/** How many brands the lens of Guestlist asks Today for at most: "My brands" of a person of many crews (ADR-306). */
export const CRM_TODAY_BRANDS_MAX = 50;

const brandOrgIds = z
  .array(z.uuid())
  .min(1)
  .max(CRM_TODAY_BRANDS_MAX)
  .refine((ids) => new Set(ids).size === ids.length, "A brand asked twice");

export const getCrmTodayInput = z.object({
  /** The sales territory of the customers counted (ADR-289): of the campaigns, the appointments and the accounts. */
  territory: listCrmAccountsInput.shape.territory,
  /**
   * The lens of the brand (ADR-306): the campaigns that invite to one of these brands, with the funnel of their
   * invitations alone; the appointments with them; the quiet customers that ever had an invitation or an order of
   * them. In a query string a comma-separated list (`?brandOrgIds=<id>,<id>`); via RPC, the array the gateway parsed.
   */
  brandOrgIds: z
    .union([
      brandOrgIds,
      z
        .string()
        .max(CRM_TODAY_BRANDS_MAX * 37)
        .transform((asked) => asked.split(",")),
    ])
    .pipe(brandOrgIds)
    .optional(),
});
export type GetCrmTodayInput = z.input<typeof getCrmTodayInput>;

/** A booked appointment of the next days: who meets which brand, in which campaign, and when. */
export const crmTodayAppointmentDto = z
  .object({
    invitationId: z.uuid(),
    /** The appointment of booking, which may meet several brands: what its sheet opens (ADR-328). */
    appointmentId: z.uuid().nullable(),
    startsAt: z.iso.datetime(),
    campaign: z.object({ id: z.uuid(), name: z.string() }),
    /** Names empty while the projection does not have the account yet. */
    customer: z.object({
      id: z.uuid(),
      name: z.string(),
      countryCode: z.string().nullable(),
      customerCode: z.string().nullable(),
    }),
    brand: z.object({ id: z.uuid(), name: z.string() }),
    /** How likely the customer is not to come, from its appointments held (ADR-316 §3); null when nothing says so. */
    noShowRisk: z.enum(noShowRisks).nullable(),
  })
  .meta({ id: "CrmTodayAppointment" });
export type CrmTodayAppointmentDto = z.output<typeof crmTodayAppointmentDto>;

/** A customer with neither an invitation nor an order that started or moved within the window. */
export const crmQuietAccountDto = crmAccountDto
  .extend({
    /** The last time one of its invitations or orders started or moved; null when it never had one. */
    lastMovementAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "CrmQuietAccount" });
export type CrmQuietAccountDto = z.output<typeof crmQuietAccountDto>;

/** A send going out or paused (ADR-316 §6): "Sending now", with how far its Workflow went. */
export const crmTodaySendDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    status: z.enum(["sending", "paused"]),
    pauseReason: z.enum(sendPauseReasons).nullable(),
    recipientCount: z.number().int(),
    sentCount: z.number().int(),
    /** Left out by the recheck of each batch, and failed for good: done as the control room counts it. */
    skippedCount: z.number().int(),
    failedCount: z.number().int(),
    batchesDone: z.number().int(),
    batches: z.number().int(),
    startedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "CrmTodaySend" });
export type CrmTodaySendDto = z.output<typeof crmTodaySendDto>;

/** Up to a limit, with how many there are in all. */
const counted = <T extends z.ZodType>(item: T) => z.object({ items: z.array(item), total: z.number().int() });

export const crmTodayDto = z
  .object({
    /**
     * The campaigns not archived, with a start date and not ended on Tomorrow's day, the first to start first, each
     * with its funnel: `invited` are the invitations nobody answered yet. With a territory, the funnel counts its
     * customers alone.
     */
    campaigns: z.object({ items: z.array(crmCampaignSummaryDto), hasMore: z.boolean() }),
    /** The booked appointments from now to `CRM_TODAY_WINDOWS.appointmentDays` ahead, the first first. */
    appointments: counted(crmTodayAppointmentDto),
    /**
     * The customers in business quiet for `CRM_TODAY_WINDOWS.quietDays`, by name: the directory of the customers, so
     * missing without `crm.account.read`.
     */
    quietAccounts: counted(crmQuietAccountDto).optional(),
    /**
     * The sends going out or paused, the first started first, through the lens (a send of Tomorrow's own kit always):
     * missing without `crm.send.read`. Their progress moves Today at most every few seconds (ADR-316 §6).
     */
    sending: z.array(crmTodaySendDto).optional(),
  })
  .meta({ id: "CrmToday" });
export type CrmTodayDto = z.output<typeof crmTodayDto>;
